import { InjectTranslationsProps } from 'ia-react-core';
import { AuthenticationContext } from 'omni-package-react-core';
import React, { FC, ReactNode, useContext, useMemo } from 'react';
import { Routes } from 'react-router-dom';
import { getPages as getPagesAccount } from 'we-package-react-account';
import { getPages as getPagesDocCentre } from 'we-package-react-document-centre';
import { AvailableDocumentsModal, DocumentsTypesResource, getPages as getPagesElectronicDocuments } from 'we-package-react-electronic-documents';
import { getPages as getPagesNews } from 'we-package-react-news';
import { getPages as getPagesPersInfo } from 'we-package-react-personal-informations';
import { getPages as getPagesPreferences } from 'we-package-react-preferences';
import { getPages as getPagesSecMsg } from 'we-package-react-secured-messaging';
import ApplicationLayout from '~/layouts/ApplicationLayout';
import ErrorLayout from '~/layouts/ErrorLayout';
import getTokenManager from '~/utils/getTokenManager';
import useIsOnboardingCompleted from '~/hooks/useIsOnboardingCompleted';
import FormLayout from '../layouts/FormLayout';
import MainLayout from '../layouts/MainLayout';
import Constants from './Constants';
import getInternalRoutes from './getInternalRoutes';

export interface AppProps { }
export interface AppComposedProps extends AppProps, InjectTranslationsProps { }

const App: FC<AppComposedProps> = () => {
  const authCtx = useContext(AuthenticationContext);
  const tokenManager = useMemo(() => getTokenManager(), []);
  const isCentralizedAuth = useIsOnboardingCompleted();
  const allPages = useMemo(
    () => ({
      electronicDocuments: getPagesElectronicDocuments(
        React.Fragment,
        authCtx?.authenticationInfo?.isAuthenticated,
        ApplicationLayout,
      ),
      personalInformation: getPagesPersInfo(
        React.Fragment,
        authCtx?.authenticationInfo?.isAuthenticated,
        tokenManager.getToken,
        FormLayout,
        Constants.PagesUrl.Profile,
      ),
      documentCentre: getPagesDocCentre(
        React.Fragment,
        authCtx?.authenticationInfo?.isAuthenticated,
      ),
      news: getPagesNews(
        React.Fragment,
        authCtx?.authenticationInfo?.isAuthenticated,
        ApplicationLayout,
      ),
      account: getPagesAccount(
        React.Fragment,
        authCtx?.authenticationInfo?.isAuthenticated,
        FormLayout,
        Constants.PagesUrl.Profile,
        isCentralizedAuth,
      ),
      securedMessaging: getPagesSecMsg(
        React.Fragment,
        authCtx?.authenticationInfo?.isAuthenticated,
        FormLayout,
        ApplicationLayout,
      ),
      preferences: getPagesPreferences(
        React.Fragment,
        authCtx?.authenticationInfo?.isAuthenticated,
        FormLayout,
        Constants.PagesUrl.Profile,
        [
          <DocumentsTypesResource key="DocumentsTypesResource" autoRequest requestOnLanguageChange />,
          <AvailableDocumentsModal key="AvailableDocumentsModal" />,
        ] as ReactNode[],
      ),
      internal: getInternalRoutes(
        React.Fragment,
        ApplicationLayout,
        ErrorLayout,
        authCtx?.authenticationInfo?.isAuthenticated,
        FormLayout,
      ),
    }),
    [authCtx?.authenticationInfo?.isAuthenticated, tokenManager.getToken, isCentralizedAuth],
  );

  return (
    <MainLayout>
      <Routes>
        <React.Fragment key="securedMessaging">{allPages.securedMessaging}</React.Fragment>
        <React.Fragment key="documentCentre">{allPages.documentCentre}</React.Fragment>
        <React.Fragment key="personalInformation">{allPages.personalInformation}</React.Fragment>
        <React.Fragment key="preferences">{allPages.preferences}</React.Fragment>
        <React.Fragment key="account">{allPages.account}</React.Fragment>
        <React.Fragment key="news">{allPages.news}</React.Fragment>
        <React.Fragment key="electronicDocuments">{allPages.electronicDocuments}</React.Fragment>
        <React.Fragment key="internal">{allPages.internal}</React.Fragment>
      </Routes>
    </MainLayout>
  );
};

export default App;
